import { averageMoodScore } from './diaryStats';
import { t } from './i18n';

const monthLabel = (monthNum) => t(`web.month.${Number(monthNum)}`);

export const formatPeriodLabel = (range = {}) => {
  const start = range.startDate ? String(range.startDate).slice(0, 10) : null;
  const end = range.endDate ? String(range.endDate).slice(0, 10) : null;
  if (!start && !end) return t('web.narrative.periodSelected');

  const [startYear, startMonth] = (start || end).split('-');
  const [endYear, endMonth] = (end || start).split('-');
  const startLabel = monthLabel(startMonth);
  const endLabel = monthLabel(endMonth);

  if (startYear === endYear && startMonth === endMonth) {
    return t('web.narrative.monthOfYear', { month: startLabel, year: startYear });
  }
  if (startYear === endYear) {
    return t('web.narrative.monthRangeSameYear', { start: startLabel, end: endLabel, year: startYear });
  }
  return t('web.narrative.monthRangeCrossYear', {
    startMonth: startLabel,
    startYear,
    endMonth: endLabel,
    endYear,
  });
};

const pluralize = (count, oneKey, manyKey) => ({
  count,
  unit: t(count === 1 ? oneKey : manyKey),
});

export const generateNarrative = (periodData = {}) => {
  const {
    range = {},
    cities = [],
    distanceByType = {},
    music = {},
    entertainment = {},
    concerts = {},
    diary = [],
  } = periodData;

  const sentences = [];

  sentences.push(t('web.narrative.intro', { period: formatPeriodLabel(range) }));

  const totalDistanceKm = Object.values(distanceByType).reduce((a, b) => a + (Number(b) || 0), 0);
  const cityNames = cities.slice(0, 3).map((c) => c.city);
  if (cityNames.length > 0 && totalDistanceKm > 0) {
    const { count, unit } = pluralize(cities.length, 'web.narrative.cityOne', 'web.narrative.cityMany');
    sentences.push(t('web.narrative.visitedCitiesKm', {
      count,
      unit,
      names: cityNames.join(', '),
      km: Math.round(totalDistanceKm),
    }));
  } else if (cityNames.length > 0) {
    const { count, unit } = pluralize(cities.length, 'web.narrative.cityOne', 'web.narrative.cityMany');
    sentences.push(t('web.narrative.visitedCities', {
      count,
      unit,
      names: cityNames.join(', '),
    }));
  } else if (totalDistanceKm > 0) {
    sentences.push(t('web.narrative.traveledKm', { km: Math.round(totalDistanceKm) }));
  }

  const totalConcerts = concerts.totalConcerts || 0;
  const totalMovies = entertainment.totalMovies || 0;
  const totalEpisodes = entertainment.totalEpisodes || 0;
  const entertainmentParts = [];
  if (totalConcerts > 0) {
    const { count, unit } = pluralize(totalConcerts, 'web.narrative.showOne', 'web.narrative.showMany');
    entertainmentParts.push(`${count} ${unit}`);
  }
  if (totalMovies > 0) {
    const { count, unit } = pluralize(totalMovies, 'web.narrative.movieOne', 'web.narrative.movieMany');
    entertainmentParts.push(`${count} ${unit}`);
  }
  if (totalEpisodes > 0) {
    const { count, unit } = pluralize(totalEpisodes, 'web.narrative.episodeOne', 'web.narrative.episodeMany');
    entertainmentParts.push(`${count} ${unit}`);
  }
  if (entertainmentParts.length > 0) {
    sentences.push(t('web.narrative.saw', { parts: entertainmentParts.join(', ') }));
  }

  const totalPlays = music.totalPlays || 0;
  if (totalPlays > 0) {
    const topArtist = (music.topArtists || [])[0];
    const artistPart = topArtist ? t('web.narrative.mainlyArtist', { artist: topArtist.artist }) : '';
    const { count, unit } = pluralize(totalPlays, 'web.narrative.trackOne', 'web.narrative.trackMany');
    sentences.push(t('web.narrative.listened', { count, unit, artistPart }));
  }

  const avgMood = averageMoodScore(diary);
  if (diary.length > 0 && avgMood !== null) {
    const rounded = Math.round(avgMood);
    const moodLabel = [1, 2, 3, 4, 5].includes(rounded) ? t(`web.mood.${rounded}`) : t('web.mood.3');
    const { count, unit } = pluralize(diary.length, 'web.narrative.entryOne', 'web.narrative.entryMany');
    sentences.push(t('web.narrative.diaryMood', { count, unit, mood: moodLabel }));
  }

  if (sentences.length === 1) {
    sentences.push(t('web.narrative.empty'));
  }

  return sentences.join(' ');
};
